<script lang="ts">import { Button } from "@infinitro/button";
import { expandGameInfo, hasGtDemo, toGameTile } from "@infinitro/catalog-min";
import { cfgCdnBase, cfgCdnTiles, cfgThemePath, FeatureSet, gameDemoMode, stateFeatureSet, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { gameLaunchedFromPath, isMobile, locale, loggedIn, ModalDialog, pushModal, selectedGameInfo, toGameSlug, toProviderSlug } from "@infinitro/global";
import { PlayIcon } from "@infinitro/icons";
import { onMount } from "svelte";
import { derived, get } from "svelte/store";
// Base CDN URL for assets
const CDN_BASE = cfgCdnBase();
const CDN_TILES = cfgCdnTiles();
// Array of colors used for fallback if no color is set for the game item
const colors = [
	"#33BCF6",
	"#FF453A",
	"#FF9E14",
	"#BF5AF3",
	"#02D280"
];
// Expose props to be passed into the component
export let item;
export let width = "";
export let maxWidth = "";
export let height = "";
export let maxHeight = "";
export let thumbnail = false;
export let thumbnailBig = false;
export let showOverlay = true;
export let isInteractive = true;
export let eagerLoad = false;
item = expandGameInfo(item);
// Flag to indicate if the placeholder should be shown
let placeholder = item.placeholder || !item.img && !item.back && !item.tile && !item.game;
// DOM elements bound for handling the image and card
let cardElement;
let imageElement;
let imageFrontElement;
let game_tile_failed = false;
let overlayElement;
$: tileSrcs = getTileSources(item.tile, $stateUi.card?.localizedTileLocales);
let tileIndex = 0;
$: tileSrc = tileSrcs[tileIndex];
let game_tile_loaded = false;
const subscriptions = new Set();
let _locale = $locale;
// Fallback function to handle image errors (e.g., broken image)
const handleErrorImage = (e) => {
	const img = e.currentTarget;
	if (!img) {
		placeholder = true;
		return;
	}
	img.onerror = null;
	placeholder = true;
	console.log(`failed to load image [${img.currentSrc || img.src}]`);
};
export function isURL(value) {
	try {
		new URL(value);
		return true;
	} catch {
		return false;
	}
}
function getTileSources(tile, localizedTileLocales) {
	if (item.prov?.toLowerCase() === "originals" && tile && !isURL(tile)) {
		return [`${CDN_TILES}/themes/${cfgThemePath()}/originals/${tile}`];
	}
	const featureSet = get(stateFeatureSet);
	const stagePath = featureSet === FeatureSet.Preview ? "/staging" : "";
	const isAnimatedGame = item.f !== undefined ? (item.f & 1 << 16) !== 0 : false;
	const animatedPath = isAnimatedGame ? "st/" : "";
	let tiles = [tile && tile.startsWith("http") ? tile : `${CDN_TILES}${stagePath}/t/${toProviderSlug(item.prov ?? "")}/${animatedPath}${toGameSlug(item.game)}`];
	if (item.prov && localizedTileLocales?.includes(_locale)) {
		const localeTile = item[_locale];
		if (localeTile && isURL(localeTile)) {
			tiles.unshift(localeTile);
		} else if (item.prov === "Sports" && tile) {
			const lastSlash = tile.lastIndexOf("/");
			tiles.unshift(tile.slice(0, lastSlash) + "/" + _locale + tile.slice(lastSlash));
		} else {
			tiles.unshift(`${CDN_TILES}${stagePath}/t/${toProviderSlug(item.prov)}/${_locale}/${animatedPath}${toGameSlug(item.game)}`);
		}
	}
	return tiles;
}
const tileLoadFail = () => {
	if (tileIndex + 1 >= tileSrcs.length) {
		// placeholder = true;
		game_tile_failed = true;
	} else {
		tileIndex++;
		tileSrc = tileSrcs[tileIndex];
	}
};
const tileLoadSuccess = () => {
	game_tile_loaded = true;
};
$: shouldShowOverlay = derived([isMobile], ([$isMobile]) => showOverlay && $isMobile && item.prov !== "Sports");
// On mobile game path: open GameInfoModal instead of launching immediately when the
// brand enables `stateUi.games.showGameInfoModalOnGamePathMobile`.
$: useGamePathInfoModalFlow = $isMobile && $stateUi.games.showGameInfoModalOnGamePathMobile;
function clickCard(e) {
	const targetIsPlayButton = e.target.parentElement?.classList.contains("play-button") || e.target.classList.contains("play-button");
	if ($shouldShowOverlay && !targetIsPlayButton) {
		if (useGamePathInfoModalFlow) {
			gameLaunchedFromPath.set(window.location.pathname);
			selectedGameInfo.set({
				game: item,
				tileSrc,
				shouldOpenOnGamePath: true
			});
		} else {
			e.preventDefault();
			e.stopPropagation();
			// Production behavior: show overlay with play buttons
			document.querySelectorAll(".game-card-slide-overlay").forEach((overlay) => {
				if (overlay !== overlayElement) {
					overlay.classList.add("hidden");
				} else {
					overlay.classList.remove("hidden");
				}
			});
			return false;
		}
	}
	gameLaunchedFromPath.set(window.location.pathname);
}
function clickSignIn(e) {
	overlayElement.classList.add("hidden");
	e.preventDefault();
	e.stopPropagation();
	pushModal(ModalDialog.Auth, { tab: "signup" });
	return false;
}
function clickDemoPlay(e) {
	gameDemoMode.set(true);
}
function clickPlay(e) {
	gameDemoMode.set(false);
}
onMount(() => {
	subscriptions.add(locale.subscribe((value) => {
		if (value === _locale) return;
		const tileLocales = get(stateUi).card?.localizedTileLocales;
		const switchIconLanguage = tileLocales?.includes(value) || tileLocales?.includes(_locale);
		_locale = value;
		if (switchIconLanguage) {
			tileIndex = 0;
			tileSrcs = getTileSources(item.tile, tileLocales);
			tileSrc = tileSrcs[tileIndex];
		}
	}));
	return () => {
		subscriptions.forEach((unsubscribe) => unsubscribe());
	};
});
</script>

<!-- Fallback container for aspect ratio -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
	class="aspect-ratio-container {thumbnail ? 'aspect-ratio-container-thumbnail' : ''} {thumbnailBig
		? 'aspect-ratio-container-thumbnail-big'
		: ''} {thumbnail || thumbnailBig ? 'no-hover' : ''}"
	style={`width: ${width}; height: ${height}; max-width: ${maxWidth}; max-height: ${maxHeight};`}
	on:click={isInteractive ? clickCard : undefined}
>
	<div
		bind:this={cardElement}
		class={item.tile && !placeholder
			? ''
			: `card ${thumbnail ? 'card-thumbnail' : ''} ${thumbnailBig ? 'card-thumbnail-big' : ''}`}
		style={item.tile && !placeholder
			? ''
			: `background-color: ${item.color ?? colors[Math.floor(Math.random() * 5)]};`}
	>
		<!-- {#if false}
			<div class="badge badge-{item.badge_type}">
				{item.badge_type === 'hot' ? 'Hot' : 'Top Pick'}
			</div>
		{/if} -->
		<div
			class={item.tile
				? `tile_item ${thumbnail ? 'time_item_thumbnail' : ''} ${thumbnailBig ? 'time_item_thumbnail_big' : ''}`
				: 'item'}
		>
			{#if placeholder}
				<div class="image-placeholder">
					<img
						src={`${CDN_BASE}/images/misc/flush-logo-medium.png`}
						alt={item.alt}
						class="placeholder-img"
						loading="lazy"
						on:error={handleErrorImage}
					/>
				</div>
			{:else if item.tile}
				<div class="tile-frame {!game_tile_failed ? '' : 'tile-frame-unloaded'}">
					<img
						src={tileSrc}
						loading={eagerLoad ? 'eager' : 'lazy'}
						alt={item.alt || `${item.game} by ${item.prov}`}
						class="image"
						style={game_tile_failed ? 'display: none;' : ''}
						on:error={tileLoadFail}
						on:load={tileLoadSuccess}
					/>
					{#if !game_tile_loaded}
						<span class="game_text">{item.game}<br />by<br />{item.prov}</span>
					{/if}
				</div>
			{:else if item.img}
				<img
					src={item.img.startsWith('http') ? item.img : `${CDN_BASE}/${item.img}`}
					alt={item.alt}
					class="image"
					loading="lazy"
					on:error={handleErrorImage}
				/>
			{/if}
			{#if placeholder && item?.game}
				<p class="name-text">{item.game}<br />by<br />{item.prov}</p>
			{/if}
		</div>
		{#if !item.tile || placeholder}
			<div class="provider-container">
				{#if item?.prov}
					<p class="provider-text">{item.prov}</p>
				{/if}
			</div>
		{/if}
	</div>
	{#if $shouldShowOverlay}
		<div class="hidden game-card-slide-overlay" bind:this={overlayElement}>
			{#if !$loggedIn}
				<Button
					width="100%"
					className="play-button game-card-slide-overlay-button"
					variant="primary"
					text={$t(Verbiage.login)}
					size="small"
					onClick={clickSignIn}
				/>
				<Button
					disabled={!item || !item.f || !hasGtDemo(item.f)}
					width="100%"
					className="play-button game-card-slide-overlay-button"
					styleCustom="font-size: 80%"
					variant="secondary"
					text={$t(Verbiage.demo_play)}
					size="small"
					onClick={clickDemoPlay}
				/>
			{:else}
				<a class="play-button game-card-slide-overlay-play" on:click={clickPlay}>
					<PlayIcon />
				</a>
			{/if}
		</div>
	{/if}
</div>

<style>
	.aspect-ratio-container {
		max-height: 148px;
		min-height: 107px;
		min-width: 78px;
		max-width: 225px;
		position: relative;
		width: 100%;
		/* Aspect ratio using padding-bottom technique for older browsers */
		padding-bottom: calc(236 / 176 * 100%);
		overflow: hidden;
		aspect-ratio: 176 / 236 !important;
		transition: transform 0.3s ease-in-out;
}

	.game-card-slide-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		padding: 0.5rem;
		gap: 0.75rem;
		border-radius: 7%;
		background-color: rgba(0, 0, 0, 0.5);
	}

	.game-card-slide-overlay.hidden {
		display: none;
	}

	:global(.game-card-slide-overlay-button p) {
		font-size: 75%;
		text-wrap: nowrap;
		white-space: nowrap;
	}

	@media (min-width: 1024px) {
		.aspect-ratio-container:hover:not(.no-hover) {
			transform: translateY(-10px);
		}
		.aspect-ratio-container:active:not(.no-hover) {
			transform: translateY(0);
		}

		.no-hover .item:hover .soon {
			opacity: 0;
			visibility: hidden;
			transform: scaleX(0);
		}
	}

	.card {
		position: relative;
		height: 100%;
		max-height: 148px;
		min-height: 107px;
		width: 100%;
		min-width: 78px;
		max-width: 225px;
		border-radius: 0.5rem;
		padding-left: 0.125rem;
		padding-right: 0.125rem;
		padding-top: 0.125rem;
		aspect-ratio: 176 / 236 !important;
		transition: transform 0.1s ease-in-out;
		transform-style: preserve-3d;
		perspective: 1000px;
		position: absolute;
		top: 0;
		left: 0;
}

	.aspect-ratio-container-thumbnail,
	.card-thumbnail {
		height: 32px;
		max-height: 32px;
		min-height: 32px;
		width: 24px;
		min-width: 24px;
		max-width: 24px;
		padding: 0px;
}

	.aspect-ratio-container-thumbnail-big,
	.card-thumbnail-big {
		height: 73px;
		max-height: 73px;
		min-height: 73px;
		width: 54px;
		min-width: 54px;
		max-width: 54px;
		padding: 0px;
}
	.game_text {
		position: absolute;
		bottom: 33.333333%;
		left: 50%;
		margin-bottom: 0.5rem;
		width: 95%;
		--tw-translate-x: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		text-align: center;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: var(--font-main);
		--tw-text-opacity: 1;
		color: rgb(107 114 128 / var(--tw-text-opacity, 1));
}

	.item {
		position: relative;
		height: 92%;
		cursor: pointer;
		overflow: hidden;
		border-radius: 0.5rem;
		transition-property: all;
		transition-duration: 200ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transform-style: preserve-3d;
}
	.tile_item {
		position: relative;
		cursor: pointer;
		overflow: hidden;
}

	.time_item_thumbnail,
	.time_item_thumbnail_big {
		border-radius: 0px;
}
	.tile-frame {
		display: flex;
		aspect-ratio: 176/236;
		height: 100%;
		width: 100%;
		cursor: pointer;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		background-color: transparent;
}
	.tile-frame-unloaded {
		border-radius: 0.75rem;
		border-width: 1px;
		--tw-border-opacity: 1;
		border-color: rgb(75 85 99 / var(--tw-border-opacity, 1));
		background-color: transparent;
}
	.tile-frame img {
		min-width: 100%;
}
	/* Hide broken image icon */
	.tile-frame img[alt='']::before,
	.tile-frame img[alt='']::after {
		display: none;
	}
	.tile-frame img[alt=''] {
		color: transparent;
	}
	.item img.image {
		min-height: 100%;
		min-width: 100%;
		-o-object-fit: cover;
		   object-fit: cover;
		transition-property: transform;
		transition-duration: 200ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		z-index: 10;
}
	.item img.image-front {
		position: absolute;
		left: 50%;
		top: 50%;
		height: 75%;
		--tw-translate-x: -50%;
		--tw-translate-y: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		-o-object-fit: cover;
		   object-fit: cover;
		transition-property: transform;
		transition-duration: 200ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		z-index: 20;
}
	.item img.image,
	.item img.image-front {
		transition: transform 0.1s ease-in-out;
		will-change: transform;
	}
	.item::before {
		content: '';
		position: absolute;
		top: 50%;
		left: 0;
		width: 100%;
		height: 55%;
		background: radial-gradient(118.34% 100% at 50.29% 100%, #121212 40%, rgba(0, 0, 0, 0) 100%);
		z-index: 30;
	}
	.soon {
		position: absolute;
		top: 0px;
		left: 0px;
		display: flex;
		height: 100%;
		width: 100%;
		align-items: center;
		justify-content: center;
		border-radius: 0.5rem;
		border-width: 2px;
		--tw-border-opacity: 1;
		border-color: rgb(255 255 255 / var(--tw-border-opacity, 1));
		background-color: rgb(0 0 0 / var(--tw-bg-opacity, 1));
		--tw-bg-opacity: 0.5;
		font-size: 1.25rem;
		line-height: 1.75rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		z-index: 30;
		transform-origin: left;
		transform: scaleX(0);
		opacity: 0;
		visibility: hidden;
		transition:
			transform 0.2s ease-in-out,
			opacity 0.2s ease-in-out,
			visibility 0.2s ease-in-out;
}
	.item:hover .soon {
		opacity: 1;
		visibility: visible;
		transform: scaleX(1);
	}
	.name-text {
		position: absolute;
		bottom: 33.333333%;
		left: 50%;
		margin-bottom: 0.5rem;
		width: 95%;
		--tw-translate-x: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		text-align: center;
		font-size: 0.875rem;
		font-weight: var(--font-main);
		text-transform: uppercase;
		line-height: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	@media (min-width: 1024px) {

		.name-text {
				font-size: 1.25rem;
				line-height: 1.75rem;
		}
}
	.name-text {
		z-index: 40;
	}
	.provider-container {
		display: flex;
		height: 8%;
		align-items: center;
		justify-content: center;
		padding: 0.25rem;
}
	.provider-text {
		margin-top: 0px;
		text-align: center;
		font-size: 6px;
		font-weight: var(--font-alternative-3);
		text-transform: uppercase;
		letter-spacing: 0.1em;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1));
}
	@media (min-width: 1024px) {

		.provider-text {
				font-size: 8px;
		}
}
	.provider-text {
		font-weight: 900;
	}

	.badge {
		position: absolute;
		top: 0.5rem;
		left: -0.375rem;
		z-index: 40;
		border-radius: 0.5rem;
		padding: 1px;
		padding-left: 0.375rem;
		padding-right: 0.375rem;
		font-size: 0.5rem;
		font-weight: 700;
		text-transform: uppercase;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1));
}

	@media (min-width: 1024px) {

		.badge {
				font-size: 0.6rem;
		}
}

	.badge-top-pick {
		background: var(--gradient-secondary);
}

	.badge-hot {
		background: var(--gradient-accent);
}

	.image-placeholder {
		display: flex;
		height: 100%;
		width: 100%;
		align-items: center;
		justify-content: center;
		--tw-bg-opacity: 1;
		background-color: rgb(0 0 0 / var(--tw-bg-opacity, 1));
}

	.placeholder-img {
		width: 50%;
}

	@media (min-width: 1024px) {

		.placeholder-img {
				width: auto;
		}
}
</style>
